import type { DailyStats, FacilitySummary, PlatformAlert } from '../types';
import { dailyStats, facilities, flags, platformAlerts } from './mockData';
import { respond } from './respond';
import { isReal, loadDailyStats, loadFacilities } from './store';
import type { Facility } from '../types';

// Super admin screens may only use DailyStats + facility metadata (no patient data).
export async function getDailyStats(facilityId: string, days = 30): Promise<DailyStats[]> {
  if (isReal()) return loadDailyStats(facilityId, days);
  return respond(dailyStats.filter((s) => s.facilityId === facilityId).slice(-days));
}

export const ESCALATION_THRESHOLDS = { urgentMinutes: 30, normalMinutes: 240 };

// Counts only: no patient fields leave this function.
function escalationAlerts(): PlatformAlert[] {
  const now = Date.now();
  const ageMin = (iso: string) => (now - new Date(iso).getTime()) / 60_000;
  return facilities.flatMap((f) => {
    const open = flags.filter((x) => x.facilityId === f.id && x.status === 'open');
    const urgent = open.filter((x) => x.urgency === 'urgent' && ageMin(x.raisedAt) > ESCALATION_THRESHOLDS.urgentMinutes).length;
    const normal = open.filter((x) => x.urgency === 'normal' && ageMin(x.raisedAt) > ESCALATION_THRESHOLDS.normalMinutes).length;
    const at = new Date().toISOString();
    const out: PlatformAlert[] = [];
    if (urgent) out.push({ id: `esc-u-${f.id}`, severity: 'red', facilityId: f.id, at, textEn: `${urgent} urgent flag(s) unacknowledged for over 30 min at ${f.displayNameEn}`, textHi: `${f.displayNameHi} में ${urgent} अति आवश्यक फ़्लैग 30 मिनट से ज़्यादा से बिना जवाब` });
    if (normal) out.push({ id: `esc-n-${f.id}`, severity: 'amber', facilityId: f.id, at, textEn: `${normal} flag(s) open for over 4 hours at ${f.displayNameEn}`, textHi: `${f.displayNameHi} में ${normal} फ़्लैग 4 घंटे से ज़्यादा से खुले` });
    return out;
  });
}

export async function getPlatformAlerts(): Promise<PlatformAlert[]> {
  if (isReal()) {
    // Counts only: open flags per clinic from the latest daily counts.
    const rows = await getPlatformSummary();
    const at = new Date().toISOString();
    return rows
      .filter((r) => r.openEscalations > 0)
      .map((r) => ({ id: `esc-${r.facilityId}`, severity: 'amber' as const, facilityId: r.facilityId, at, textEn: `${r.openEscalations} flag(s) open at ${r.name}`, textHi: `${r.nameHi} में ${r.openEscalations} फ़्लैग खुले` }));
  }
  return respond([...escalationAlerts(), ...platformAlerts]);
}

export async function getFacilitySummary(facilityId: string): Promise<FacilitySummary> {
  const s = (await getPlatformSummary()).find((x) => x.facilityId === facilityId);
  if (!s) throw new Error('Facility not found');
  return s;
}

const sum = (rows: DailyStats[], k: keyof DailyStats) => rows.reduce((a, r) => a + (r[k] as number), 0);

export async function getPlatformSummary(): Promise<FacilitySummary[]> {
  if (isReal()) {
    const fs = await loadFacilities();
    const stats = await Promise.all(fs.map((f) => loadDailyStats(f.id, 90).catch(() => [] as DailyStats[])));
    return summarise(fs, (fid) => stats[fs.findIndex((f) => f.id === fid)] ?? []);
  }
  return respond(summarise(facilities, (fid) => dailyStats.filter((s) => s.facilityId === fid)));
}

function summarise(list: Facility[], statsFor: (fid: string) => DailyStats[]): FacilitySummary[] {
  const month = new Date().toISOString().slice(0, 7);
  return list.map((f) => {
    const rows = statsFor(f.id);
    // New facilities have no stats rows yet: fall back to zeros instead of crashing.
    const zero = { patientsEnrolled: 0, onTimeRate: 0, openEscalations: 0 } as DailyStats;
    const last = rows[rows.length - 1] ?? zero;
    const weekAgo = rows[Math.max(0, rows.length - 8)] ?? zero;
    const monthRows = rows.filter((r) => r.date.startsWith(month));
    const callsMade = sum(rows, 'callsMade');
    const callsAnswered = sum(rows, 'callsAnswered');
    const whatsappSent = sum(rows, 'whatsappSent');
    return {
      facilityId: f.id,
      name: f.displayNameEn,
      nameHi: f.displayNameHi,
      city: f.city,
      status: f.status,
      enrolled: last.patientsEnrolled,
      newThisWeek: last.patientsEnrolled - weekAgo.patientsEnrolled,
      callsMade,
      callsAnswered,
      answerRate: callsMade ? Math.round((callsAnswered / callsMade) * 100) : 0,
      whatsappSent,
      whatsappRead: sum(rows, 'whatsappRead'),
      onTimeRate: last.onTimeRate,
      baselineOnTimeRate: f.baselineOnTimeRate,
      openEscalations: last.openEscalations,
      callMinutesMonth: sum(monthRows, 'callMinutes'),
      estimatedCostMonth: sum(monthRows, 'estimatedCost'),
      hasData: rows.length > 0,
    };
  });
}
